import { validateDueDateRange } from '../../../../../utils/utils';
import {
  LEGACY_TASK_TYPE_LABEL_CHECKLIST,
  LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME,
  SIGN_OFF,
  USER_ROLE,
  APPROVER_TAG,
  EVIDENCE_REQUIRED,
  isEvidenceRequiredEnabled,
} from '../../../../../utils/constants';
import {
  activeUserIdsFromUserRoles,
  normalizeUserIdList,
} from '../../../../Setup/TaskList/utils/assignmentRoles';
import { findDuplicateValues, duplicateInRoleMessage } from '../../../../../utils/bulkUploadValidationMessages';

function roleValuesForDuplicateCheck(ids = [], users = []) {
  const emailById = new Map();
  (Array.isArray(users) ? users : []).forEach((user) => {
    if (!user || user._id == null) return;
    const email = String(user.email || '').trim();
    emailById.set(String(user._id), email || String(user._id));
  });
  return (Array.isArray(ids) ? ids : []).map((id) => emailById.get(String(id)) || String(id || ''));
}

/** Suffix shown next to inactive users in Add/Edit Task assignment pickers. */
export const INACTIVE_USER_NAME_SUFFIX = ' (inactive)';

function userRecordId(user) {
  if (user == null) return '';
  if (typeof user === 'object') return String(user._id || user.id || '');
  return String(user);
}

function normalizeIsActiveFlag(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
  }
  return null;
}

/** Explicit inactive on the user record (boolean, 0/1, or `inactive: true`). */
export function userHasExplicitInactiveFlag(user) {
  if (!user || typeof user !== 'object') return false;
  if (user.inactive === true) return true;
  return normalizeIsActiveFlag(user.isActive) === false;
}

/** Explicit active on the user record (boolean true or 1). */
export function userHasExplicitActiveFlag(user) {
  if (!user || userHasExplicitInactiveFlag(user)) return false;
  return normalizeIsActiveFlag(user.isActive) === true;
}

/**
 * Active catalog for assignment dropdowns.
 * Drops known-inactive IDs; uses per-user isActive when present; otherwise
 * intersects with store active IDs. Falls back to storeActiveUsers if the
 * source list cannot be filtered (Setup getformdata often has no flags).
 */
export function filterActiveAssignmentCatalog({
  users = [],
  activeIdSet,
  inactiveIdSet,
  storeActiveUsers = [],
} = {}) {
  const list = Array.isArray(users) ? users : [];
  const activeIds = activeIdSet instanceof Set ? activeIdSet : new Set(activeIdSet || []);
  const inactiveIds = inactiveIdSet instanceof Set ? inactiveIdSet : new Set(inactiveIdSet || []);
  const storeActive = Array.isArray(storeActiveUsers) ? storeActiveUsers : [];

  const keepIfNotKnownInactive = (user) => {
    if (!user) return false;
    if (userHasExplicitInactiveFlag(user)) return false;
    const id = userRecordId(user);
    if (id && inactiveIds.has(id)) return false;
    return true;
  };

  const anyExplicitStatus = list.some(
    (user) => userHasExplicitActiveFlag(user) || userHasExplicitInactiveFlag(user)
  );

  let catalog;
  if (anyExplicitStatus) {
    catalog = list.filter((user) => keepIfNotKnownInactive(user) && userHasExplicitActiveFlag(user));
  } else if (activeIds.size) {
    catalog = list.filter((user) => {
      const id = userRecordId(user);
      return keepIfNotKnownInactive(user) && id && activeIds.has(id);
    });
  } else {
    catalog = [];
  }

  if (!catalog.length && storeActive.length) {
    return storeActive.filter(keepIfNotKnownInactive);
  }
  return catalog;
}

export function isActiveAssignmentUser(user, activeIdSet) {
  const id = userRecordId(user);
  if (id && activeIdSet instanceof Set && activeIdSet.has(id)) return true;
  if (userHasExplicitActiveFlag(user)) return true;
  return false;
}

export function formatInactiveAssignmentUserName(user) {
  const name = String(user?.name || user?.displayname || '').trim();
  if (!name) return `Unknown${INACTIVE_USER_NAME_SUFFIX}`;
  const withoutSuffix = name.replace(/\s*\(inactive\)$/i, '').trim();
  return `${withoutSuffix}${INACTIVE_USER_NAME_SUFFIX}`;
}

export function buildKnownUserMap(userLists = []) {
  const map = new Map();
  (Array.isArray(userLists) ? userLists : [userLists]).forEach((list) => {
    (Array.isArray(list) ? list : []).forEach((user) => {
      const id = userRecordId(user);
      if (id) map.set(id, user);
    });
  });
  return map;
}

/** Keep IDs that resolve to a real user record (drops leftover non-user object IDs). */
export function filterAssignmentIdsToKnownUsers(ids, knownIds) {
  const known = knownIds instanceof Set ? knownIds : new Set(knownIds || []);
  return normalizeUserIdList(Array.isArray(ids) ? ids : (ids ? [ids] : []))
    .filter((id) => known.has(id));
}

/**
 * Dropdown options for preparer/approver/sign-off.
 * Add: active users only.
 * Edit: active users plus already-selected inactive users that exist in the user catalog.
 */
/**
 * Merge already-selected assignees into location-scoped picker users so chips
 * stay visible when location access changes; menu filtering stays caller-side.
 */
export function mergeSelectedAssignmentPickerUsers(scopedUsers = [], selectedIds = [], knownUserMap = new Map()) {
  const scoped = Array.isArray(scopedUsers) ? scopedUsers : [];
  const scopedIdSet = new Set(scoped.map((user) => userRecordId(user)).filter(Boolean));
  const extras = [];

  normalizeUserIdList(selectedIds).forEach((id) => {
    if (scopedIdSet.has(id)) return;
    const user = knownUserMap.get(id);
    if (!user) return;
    const name = String(user.name || user.displayname || '').trim() || id;
    extras.push({
      ...user,
      _id: id,
      name,
      displayname: name,
    });
  });

  return [...scoped, ...extras];
}

export function buildAssignmentUserOptions({
  activeUsers = [],
  allUsers = [],
  selectedIds = [],
  isEdit = false,
  activeIdSet: activeIdSetArg,
} = {}) {
  const activeList = Array.isArray(activeUsers) ? activeUsers : [];
  const activeIdSet = activeIdSetArg instanceof Set
    ? activeIdSetArg
    : new Set(activeList.map((user) => userRecordId(user)).filter(Boolean));
  const byId = new Map();

  activeList.forEach((user) => {
    const id = userRecordId(user);
    if (!id) return;
    byId.set(id, { ...user, _id: id, inactive: false });
  });

  if (!isEdit) return Array.from(byId.values());

  const known = buildKnownUserMap([allUsers, activeList]);
  normalizeUserIdList(Array.isArray(selectedIds) ? selectedIds : []).forEach((id) => {
    if (byId.has(id)) return;
    const user = known.get(id);
    if (!user) return;
    const inactive = !isActiveAssignmentUser(user, activeIdSet);
    const displayName = inactive
      ? formatInactiveAssignmentUserName(user)
      : (user.name || user.displayname || '');
    byId.set(id, {
      ...user,
      _id: id,
      name: displayName,
      displayname: displayName,
      inactive,
    });
  });

  return Array.from(byId.values());
}

export function formHasInactiveAssignmentUsers(data, inactiveIdSet) {
  return assignmentHasInactiveUser(data?.preparer, inactiveIdSet)
    || assignmentHasInactiveUser(data?.approver, inactiveIdSet)
    || assignmentHasInactiveUser(data?.sign_off_approvers, inactiveIdSet);
}

export function inactiveAssignmentIdSet(users = []) {
  return new Set(
    (Array.isArray(users) ? users : [])
      .filter((user) => user && user.inactive === true)
      .map((user) => userRecordId(user))
      .filter(Boolean),
  );
}

function assignmentHasInactiveUser(ids, inactiveIdSet) {
  if (!inactiveIdSet || !inactiveIdSet.size) return false;
  return normalizeUserIdList(Array.isArray(ids) ? ids : (ids ? [ids] : []))
    .some((id) => inactiveIdSet.has(id));
}

/**
 * Normalize API / display task_type labels to canonical form values used by the dialog.
 * Maps legacy "Normal Task" to the current Checklist stored value.
 * @param {string} taskType
 * @param {{ NORMAL: string, ACCOUNT_RECONCILIATION: string }} taskTypes
 */
export function normalizeTaskTypeForForm(taskType, taskTypes = {}) {
  if (!taskType) return '';
  const trimmed = String(taskType).trim();
  const lower = trimmed.toLowerCase();

  if (
    lower === LEGACY_TASK_TYPE_LABEL_CHECKLIST.toLowerCase()
    || lower === LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME.toLowerCase()
  ) {
    return taskTypes.NORMAL || trimmed;
  }
  if (
    taskType === taskTypes.ACCOUNT_RECONCILIATION
    || lower === 'account reconciliation'
  ) {
    return taskTypes.ACCOUNT_RECONCILIATION || trimmed;
  }
  return taskType;
}

/** Empty-state copy for preparer/approver dropdowns in add-task assignment. */
export const ADD_TASK_ASSIGNMENT_EMPTY_MESSAGES = {
  SELECT_LOCATION_BEFORE_USERS: 'select location before selecting preparer and approver',
  NO_USERS_FOUND: 'No users found',
};

/** UI placeholders for add-task detail fields. */
export const ADD_TASK_FIELD_PLACEHOLDERS = {
  TASK_NAME: 'e.g., Conduct Q3 Financial Audit',
  PURPOSE: 'Enter the primary goal or reason for this task...',
  INSTRUCTION: 'Enter detailed instructions for how this task should be completed...',
  CUSTOM_FIELD: 'Type here..',
};

/** Default empty model for add-task flow (no _id). */
export function createEmptyAddTaskData() {
  return {
    description: '',
    category: '',
    subcategory: '',
    task_type: '',
    priority: '',
    location: '',
    preparer: [],
    approver: [],
    requires_sign_off: false,
    sign_off_approvers: [],
    no_of_evidence: EVIDENCE_REQUIRED.COUNT_MIN,
    evidence_required: false,
    tags: [],
    assertions: [],
    purpose: '',
    instruction: '',
    remark: '',
    glcode: [],
    __tbtotals: { items: [] },
    __tblinks: [],
    customfields: [],
    threshold: '',
    enforceThreshold: true,
    auto_approve: false,
    duedate: '',
  };
}

const EVIDENCE_COUNT_NAV_KEYS = new Set([
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'Home', 'End',
]);

/** True when the key would keep No. of Evidence within 0–10. */
export function isEvidenceCountKeyAllowed(event, currentValue) {
  if (event.ctrlKey || event.metaKey || event.altKey) return true;
  if (EVIDENCE_COUNT_NAV_KEYS.has(event.key)) return true;
  if (event.key.length !== 1 || !/^\d$/.test(event.key)) return false;
  const el = event.target;
  const raw = String(currentValue ?? '');
  const start = Number.isInteger(el?.selectionStart) ? el.selectionStart : raw.length;
  const end = Number.isInteger(el?.selectionEnd) ? el.selectionEnd : start;
  const next = `${raw.slice(0, start)}${event.key}${raw.slice(end)}`;
  const num = Number(next);
  return Number.isInteger(num)
    && num >= EVIDENCE_REQUIRED.COUNT_MIN
    && num <= EVIDENCE_REQUIRED.COUNT_MAX;
}

/** Parse a paste payload to a 0–10 integer, or null if invalid. */
export function parsePastedEvidenceCount(text) {
  const digits = String(text || '').replace(/\D/g, '');
  if (!digits) return null;
  const num = Number(digits);
  if (!Number.isInteger(num) || num < EVIDENCE_REQUIRED.COUNT_MIN || num > EVIDENCE_REQUIRED.COUNT_MAX) {
    return null;
  }
  return num;
}

/** Clamp evidence count to integer 0–10. */
export function clampEvidenceInput(val) {
  if (val === '' || val === null || val === undefined) {
    return { value: 0, error: '' };
  }
  const num = Number(val);
  if (Number.isNaN(num)) {
    return { value: 0, error: '' };
  }
  if (!Number.isInteger(num)) {
    return { value: 0, error: EVIDENCE_REQUIRED.COUNT_RANGE_ERROR };
  }
  if (num < EVIDENCE_REQUIRED.COUNT_MIN) {
    return { value: EVIDENCE_REQUIRED.COUNT_MIN, error: EVIDENCE_REQUIRED.COUNT_RANGE_ERROR };
  }
  if (num > EVIDENCE_REQUIRED.COUNT_MAX) {
    return { value: EVIDENCE_REQUIRED.COUNT_MAX, error: EVIDENCE_REQUIRED.COUNT_RANGE_ERROR };
  }
  return { value: num, error: '' };
}

/**
 * Sync evidence count + validation after Required toggle or input change.
 * When Required is ON, count must be 1–10 (toggle fills COUNT_DEFAULT if 0).
 * When Required is OFF, keep the entered count unless resetCountWhenNotRequired (then 0).
 * @param {object} data - { evidence_required, no_of_evidence }
 * @param {{ fillDefaultWhenRequired?: boolean, resetCountWhenNotRequired?: boolean }} [options]
 * @returns {{ no_of_evidence: number, error: string|string[] }}
 */
export function resolveEvidenceFieldState(data = {}, options = {}) {
  if (!isEvidenceRequiredEnabled(data.evidence_required) && options.resetCountWhenNotRequired) {
    return { no_of_evidence: EVIDENCE_REQUIRED.COUNT_MIN, error: '' };
  }
  const { value, error } = clampEvidenceInput(data.no_of_evidence);
  if (error) {
    return { no_of_evidence: value, error: [error] };
  }
  if (isEvidenceRequiredEnabled(data.evidence_required) && value < 1) {
    if (options.fillDefaultWhenRequired) {
      return { no_of_evidence: EVIDENCE_REQUIRED.COUNT_DEFAULT, error: '' };
    }
    return { no_of_evidence: value, error: [EVIDENCE_REQUIRED.COUNT_REQUIRED_ERROR] };
  }
  return { no_of_evidence: value, error: '' };
}

/**
 * Validates add-task form fields. Returns { valid, errors }.
 * @param {Object} data - addTaskData
 * @param {Object} context - { customColumns, selectGls, coaCategoryAcc, coaSubcategoryAcc, dueDateMinDate, dueDateMaxDate, dueDateUsesCycleEnd, maxDueDateDays, errorMessages, taskTypes }
 */
export function validateTaskFields(data, context = {}) {
  const errors = {};
  const d = data || {};
  const EM = context.errorMessages || {};
  const TASK_TYPES = context.taskTypes || {};
  const req = (key, msg) => {
    errors[key] = [msg || 'required'];
  };

  if (!d.description || !String(d.description).trim()) {
    req('description', EM.TASK_DESCRIPTION_REQUIRED);
  }
  if (!d.category) req('category', EM.CATEGORY_REQUIRED);
  if (!d.subcategory) req('subcategory', EM.SUBCATEGORY_REQUIRED);
  if (!d.task_type) req('task_type', EM.TASK_TYPE_REQUIRED);
  if (!d.priority) req('priority', EM.PRIORITY_REQUIRED);

  const preparerArr = Array.isArray(d.preparer) ? d.preparer : (d.preparer ? [d.preparer] : []);
  const approverArr = Array.isArray(d.approver) ? d.approver : (d.approver ? [d.approver] : []);
  if (!d.location) req('location', EM.LOCATION_REQUIRED);
  if (!preparerArr.length) req('preparer', EM.PREPARER_REQUIRED);
  if (!approverArr.length) req('approver', EM.APPROVER_REQUIRED);

  const users = context.users || [];
  const duplicatePreparers = findDuplicateValues(roleValuesForDuplicateCheck(preparerArr, users));
  if (duplicatePreparers.length) {
    req('preparer', duplicateInRoleMessage(duplicatePreparers, 'preparer'));
  }
  const duplicateApprovers = findDuplicateValues(roleValuesForDuplicateCheck(approverArr, users));
  if (duplicateApprovers.length) {
    req('approver', duplicateInRoleMessage(duplicateApprovers, 'approver'));
  }

  if (!d.duedate) {
    req('duedate', EM.DUE_DATE_REQUIRED || 'Due Date is required');
  } else {
    const dueDateResult = validateDueDateRange(d.duedate, context.maxDueDateDays, {
      minDate: context.dueDateMinDate,
      maxDate: context.dueDateMaxDate,
      usesCycleEnd: context.dueDateUsesCycleEnd === true,
    });
    if (!dueDateResult.valid) {
      req('duedate', dueDateResult.error);
    }
  }

  const evidenceResult = clampEvidenceInput(d.no_of_evidence);
  if (evidenceResult.error) {
    req('no_of_evidence', evidenceResult.error);
  } else if (
    isEvidenceRequiredEnabled(d.evidence_required)
  ) {
    const count = Number(evidenceResult.value);
    if (!Number.isFinite(count) || count < 1) {
      req('no_of_evidence', EVIDENCE_REQUIRED.COUNT_REQUIRED_ERROR);
    }
  }

  if (Array.isArray(context.customColumns)) {
    context.customColumns.forEach((col) => {
      if (col.isRequired && (d[col.columnName] === undefined || d[col.columnName] === null || d[col.columnName] === '')) {
        req(col.columnName, `${col.displayText} is required`);
      }
    });
  }

  if (preparerArr.length && approverArr.length) {
    const preparerSet = new Set(preparerArr.map((x) => String(x)));
    const overlap = approverArr.filter((x) => preparerSet.has(String(x)));
    if (overlap.length) {
      req('location_users', EM.PREPARER_APPROVER_OVERLAP);
    }
  }

  const requiresSignOff = !!d.requires_sign_off;
  const signOffArr = normalizeUserIdList(d.sign_off_approvers);
  if (requiresSignOff && !signOffArr.length) {
    req('sign_off_approvers', SIGN_OFF.ERRORS.REQUIRED_WHEN_ENABLED);
  }
  if (signOffArr.length && !approverArr.length) {
    req('sign_off_approvers', SIGN_OFF.ERRORS.ORPHAN_L2);
  }
  if (signOffArr.length) {
    const duplicateSignOff = findDuplicateValues(roleValuesForDuplicateCheck(signOffArr, users));
    if (duplicateSignOff.length) {
      req('sign_off_approvers', duplicateInRoleMessage(duplicateSignOff, 'sign-off approver'));
    }
    const preparerOverlap = signOffArr.filter((id) => preparerArr.includes(id));
    if (preparerOverlap.length) {
      req('sign_off_approvers', SIGN_OFF.ERRORS.PREPARER_SOD);
    }
    const approverOverlap = signOffArr.filter((id) => approverArr.includes(id));
    if (approverOverlap.length) {
      req('sign_off_approvers', SIGN_OFF.ERRORS.APPROVER_SOD);
    }
  }

  const selectGls = context.selectGls || [];
  const coaCategoryAcc = context.coaCategoryAcc || [];
  const coaSubcategoryAcc = context.coaSubcategoryAcc || [];

  if (d.task_type === TASK_TYPES.ACCOUNT_RECONCILIATION) {
    if (!selectGls.length) {
      req('glcode', EM.GL_REQUIRED_RECON || EM.GL_CODE_REQUIRED);
    }
    if (d.category && !coaCategoryAcc.includes(d.category)) {
      req('category', EM.CATEGORY_NOT_FROM_COA);
    }
    if (d.subcategory && !coaSubcategoryAcc.includes(d.subcategory)) {
      req('subcategory', EM.SUBCATEGORY_NOT_FROM_COA);
    }
  } else if (d.task_type === TASK_TYPES.NORMAL) {
    if (selectGls.length) {
      req('glcode', EM.GL_NOT_ALLOWED_NORMAL || 'GL codes are not allowed for Checklist tasks');
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

export const ADD_TASK_STEPS = {
  DETAILS: 'details',
  ACCOUNTS: 'accounts',
};

/**
 * Coerce category/subcategory master lists to unique strings.
 * getformdata / uniquecategories can mix primitives with objects;
 * object items break checklist combobox selection because lb-dropdown
 * defaults to item-value "value".
 */
export function toOptionStrings(list = []) {
  const out = [];
  const seen = new Set();
  (Array.isArray(list) ? list : []).forEach((item) => {
    let val = '';
    if (typeof item === 'string' || typeof item === 'number') {
      val = String(item).trim();
    } else if (item && typeof item === 'object') {
      val = String(
        item.name || item.label || item.displayText || item.value
        || item.category || item.subcategory || '',
      ).trim();
    }
    if (!val) return;
    const key = val.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(val);
  });
  return out;
}

/** Keep the current edit value visible when it is missing from master lists. */
export function ensureCurrentValueInItems(items, currentValue) {
  const list = Array.isArray(items) ? items : [];
  const current = typeof currentValue === 'string' || typeof currentValue === 'number'
    ? String(currentValue).trim()
    : '';
  if (current && !list.includes(current)) {
    return [current, ...list];
  }
  return list;
}

const DETAIL_KEYS = [
  'description',
  'category',
  'subcategory',
  'task_type',
  'priority',
  'duedate',
  'no_of_evidence',
  'location',
  'preparer',
  'approver',
  'sign_off_approvers',
  'location_users',
];

const ACCOUNTS_KEYS = ['glcode'];

/** Field keys validated for a given stepper step. */
export function getStepValidationKeys(step, context = {}) {
  if (step === ADD_TASK_STEPS.DETAILS) {
    return [
      ...DETAIL_KEYS,
      ...(Array.isArray(context.customColumns)
        ? context.customColumns.filter((c) => c.isRequired).map((c) => c.columnName)
        : []),
    ];
  }
  return [...ACCOUNTS_KEYS];
}

/**
 * Validate a single stepper step. Returns { valid, errors } for that step only.
 * @param {'details'|'accounts'} step
 */
export function validateTaskStep(step, data, context = {}) {
  const { errors: allErrors } = validateTaskFields(data, context);
  const keys = getStepValidationKeys(step, context);

  const errors = {};
  keys.forEach((key) => {
    if (allErrors[key]) errors[key] = allErrors[key];
  });

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

/** Hydrate sign-off fields from task API projections / user_roles. */
export function hydrateSignOffFromTask(task = {}) {
  const fromRoles = activeUserIdsFromUserRoles(
    task.user_roles,
    USER_ROLE.APPROVER,
    APPROVER_TAG.L2,
  );
  const signOffApprovers =
    Array.isArray(task.user_roles) && task.user_roles.length
      ? fromRoles
      : normalizeUserIdList(task.sign_off_approvers).length
        ? normalizeUserIdList(task.sign_off_approvers)
        : fromRoles;
  const approvalState = task.approval_state || {};
  let requiresSignOff = false;
  if (task.requires_sign_off != null) {
    requiresSignOff = !!task.requires_sign_off;
  } else if (approvalState.l2_required != null) {
    requiresSignOff = !!approvalState.l2_required;
  } else {
    requiresSignOff = signOffApprovers.length > 0;
  }
  return {
    requires_sign_off: requiresSignOff,
    sign_off_approvers: requiresSignOff ? signOffApprovers : [],
  };
}

/** Closed via L2 sign-off — no assignment edits allowed. */
export function isL2ClosedTask(task) {
  const approvalState = task?.approval_state || {};
  return task?.stage === 3 && approvalState.l2_completed === true;
}
